import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  showCloseButton?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const container = typeof document !== 'undefined' ? document.body : null
  if (!container) return null

  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' }
  const dialogMaxHeight = 'min(90vh, calc(100dvh - 24px))'

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn overflow-y-auto overflow-x-hidden"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-lg shadow-2xl w-full ${sizeClasses[size]} transform transition-all duration-300 animate-slideUp flex flex-col my-auto shrink-0 overflow-hidden`}
        style={{
          height: dialogMaxHeight,
          maxHeight: dialogMaxHeight,
        }}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 shrink-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 truncate pr-2">{title}</h2>
          {showCloseButton && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors shrink-0" aria-label="Close modal">
              <X size={24} />
            </button>
          )}
        </div>
        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="modal-body"
        >
          {children}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  )

  try {
    return createPortal(modalContent, container)
  } catch (err) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('Modal createPortal failed:', err)
    }
    return null
  }
}
